import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { MarsPriceTier } from '@/lib/mars-prices';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface EmailConnection {
  id: string;
  provider: 'gmail' | 'outlook';
  email_address: string;
  is_active: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  created_at: string;
}

export interface ConnectionStatus {
  connections: EmailConnection[];
  gmail: EmailConnection | null;
  outlook: EmailConnection | null;
}

export interface EirsaliyeRow {
  id: string;
  company_id: string;
  email_id: string | null;
  attachment_id: string | null;
  ettn: string;
  irsaliye_no: string;
  erp_irsaliye_no: string | null;
  irsaliye_tarihi: string;
  irsaliye_zamani: string | null;
  sevk_tarihi: string | null;
  sevk_zamani: string | null;
  senaryo: string | null;
  irsaliye_tipi: string | null;
  teslim_adres: string | null;
  sube: string | null;
  tasima_no: string | null;
  siparis_teslimat_no: string | null;
  musteri_siparis_no: string | null;
  bayi_kodu: string | null;
  toplam_adet: number | null;
  tasiyici_firma_vkn: string | null;
  tasiyici_firma_unvan: string | null;
  arac_plaka: string | null;
  dorse_plaka: string | null;
  sofor_ad: string | null;
  sofor_tckn: string | null;
  sofor_telefon: string | null;
  cikis_depo_adres: string | null;
  parsed_by: string;
  parse_confidence: number | null;
  raw_text: string | null;
  status: string;
  /** True when every line has an inventory movement from e-Irsaliye import (stoga alindi). */
  all_items_stock_imported?: boolean;
  estimated_total_cost?: number | null;
  created_at: string;
  updated_at: string;
}

export interface EirsaliyeItemRow {
  id: string;
  eirsaliye_id: string;
  sira_no: number;
  malzeme_no: string | null;
  malzeme_tanimi: string;
  miktar: number;
  olcu_birimi: string;
  matched_product_id: string | null;
  stock_imported?: boolean;
  stock_imported_at?: string | null;
  manual_received?: boolean;
  manual_received_at?: string | null;
  received_mode?: 'stock_import' | 'manual_received' | null;
  created_at: string;
}

export interface EirsaliyeDetail extends EirsaliyeRow {
  eirsaliye_items: EirsaliyeItemRow[];
  eirsaliye_emails?: {
    from_address: string;
    subject: string;
    received_at: string;
    provider_message_id: string;
  };
  eirsaliye_attachments?: {
    file_name: string;
    file_size_bytes: number;
    storage_path: string;
  };
}

export interface SyncLogRow {
  id: string;
  connection_id: string | null;
  started_at: string;
  finished_at: string | null;
  status: string;
  emails_found: number;
  emails_processed: number;
  irsaliyes_created: number;
  errors: any[];
  triggered_by: string;
}

export interface EirsaliyeStats {
  total_count: number;
  this_month_count: number;
  total_adet: number;
  needs_review_count: number;
  last_sync_at: string | null;
}

export interface DailyTotals {
  date: string;
  irsaliye_count: number;
  total_adet: number;
  unique_products: number;
}

export interface DailyStockImportPrefillItem {
  eirsaliye_item_id: string;
  eirsaliye_no: string;
  product_code: string;
  product_name: string;
  category: string;
  quantity: number;
  cost: number;
  cost_source:
    | 'mars_product_prices.pesin_price'
    | 'mars_product_prices.price_30'
    | 'mars_product_prices.price_60'
    | 'mars_product_prices.price_90'
    | 'mars_product_prices.price_120'
    | 'mars_product_prices.price_150'
    | 'mars_product_prices.price_180'
    | 'products.current_cost'
    | 'products.initial_cost'
    | 'fallback_zero';
  cost_source_label: string;
  matched_product_id: string | null;
}

export interface SyncResult {
  success: boolean;
  results: Array<{
    provider: string;
    status?: string;
    emails_found?: number;
    emails_processed?: number;
    irsaliyes_created?: number;
    error_count?: number;
    error?: string;
  }>;
}

// ─── Internal helpers ───────────────────────────────────────────────────────

type MarsPriceLookupRow = {
  sku: string;
  pesin_price: number | string | null;
  price_30: number | string | null;
  price_60: number | string | null;
  price_90: number | string | null;
  price_120: number | string | null;
  price_150: number | string | null;
  price_180: number | string | null;
  price_date: string;
};

type EirsaliyeItemUpdate = Database['public']['Tables']['eirsaliye_items']['Update'];

type MarsPriceColumn =
  | 'pesin_price'
  | 'price_30'
  | 'price_60'
  | 'price_90'
  | 'price_120'
  | 'price_150'
  | 'price_180';

const MARS_PRICE_COLUMN_MAP: Record<MarsPriceTier, MarsPriceColumn> = {
  pesin: 'pesin_price',
  '30': 'price_30',
  '60': 'price_60',
  '90': 'price_90',
  '120': 'price_120',
  '150': 'price_150',
  '180': 'price_180',
};

function toMarsCostSource(tier: MarsPriceTier): DailyStockImportPrefillItem['cost_source'] {
  const column = MARS_PRICE_COLUMN_MAP[tier];
  return `mars_product_prices.${column}` as DailyStockImportPrefillItem['cost_source'];
}

function normalizeSku(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

function asNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatCostSourceLabel(source: DailyStockImportPrefillItem['cost_source']): string {
  switch (source) {
    case 'mars_product_prices.pesin_price':
      return 'Mars fiyat (pesin)';
    case 'mars_product_prices.price_30':
      return 'Mars fiyat (30 gun)';
    case 'mars_product_prices.price_60':
      return 'Mars fiyat (60 gun)';
    case 'mars_product_prices.price_90':
      return 'Mars fiyat (90 gun)';
    case 'mars_product_prices.price_120':
      return 'Mars fiyat (120 gun)';
    case 'mars_product_prices.price_150':
      return 'Mars fiyat (150 gun)';
    case 'mars_product_prices.price_180':
      return 'Mars fiyat (180 gun)';
    case 'products.current_cost':
      return 'Products son maliyet';
    case 'products.initial_cost':
      return 'Products ilk maliyet';
    default:
      return 'Kaynak yok (0)';
  }
}

async function getValidAccessToken(): Promise<string> {
  let { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    throw new Error(sessionError.message || 'Unable to read session');
  }

  // Refresh slightly before expiry to avoid race conditions on function calls.
  const expiresSoon =
    !!session?.expires_at && session.expires_at * 1000 - Date.now() < 60 * 1000;

  if (!session?.access_token || expiresSoon) {
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshed.session?.access_token) {
      throw new Error('Session expired. Please sign in again.');
    }
    session = refreshed.session;
  }

  return session.access_token;
}

async function invokeAuthedFunction<T>(
  functionName: string,
  body: Record<string, unknown>,
): Promise<{ data: T | null; error: Error | null }> {
  const accessToken = await getValidAccessToken();

  let { data, error } = await supabase.functions.invoke(functionName, {
    body,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  // Retry once with a forced refresh if token was rejected by the edge gateway.
  const maybeStatus = (error as any)?.context?.status;
  const unauthorizedByMessage = error?.message?.toLowerCase().includes('unauthorized');
  if (maybeStatus === 401 || unauthorizedByMessage) {
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (!refreshError && refreshed.session?.access_token) {
      ({ data, error } = await supabase.functions.invoke(functionName, {
        body,
        headers: {
          Authorization: `Bearer ${refreshed.session.access_token}`,
        },
      }));
    }
  }

  return {
    data: (data as T | null) ?? null,
    error: (error as Error | null) ?? null,
  };
}

async function fetchMarsPriceMapBySku(
  skus: string[],
  maxDateInclusive: string,
): Promise<Map<string, MarsPriceLookupRow[]>> {
  const skuList = Array.from(new Set(skus.map(normalizeSku).filter(Boolean)));
  if (!skuList.length) return new Map();

  const { data, error } = await supabase
    .from('mars_product_prices')
    .select('sku, pesin_price, price_30, price_60, price_90, price_120, price_150, price_180, price_date')
    .in('sku', skuList)
    .lte('price_date', maxDateInclusive)
    .order('sku', { ascending: true })
    .order('price_date', { ascending: false });

  if (error || !data?.length) {
    if (error) console.error('Mars price fetch failed:', error);
    return new Map();
  }

  const grouped = new Map<string, MarsPriceLookupRow[]>();
  for (const row of data as MarsPriceLookupRow[]) {
    const sku = normalizeSku(row.sku);
    if (!sku) continue;
    if (!grouped.has(sku)) grouped.set(sku, []);
    grouped.get(sku)!.push(row);
  }
  return grouped;
}

function pickMarsCostForDate(
  rows: MarsPriceLookupRow[] | undefined,
  targetDate: string,
  tier: MarsPriceTier = 'pesin',
): number | null {
  if (!rows?.length) return null;
  const priceColumn = MARS_PRICE_COLUMN_MAP[tier];

  for (const row of rows) {
    if (row.price_date <= targetDate) {
      const price = asNumber(row[priceColumn]);
      return price > 0 ? price : 0;
    }
  }
  return null;
}

async function fetchImportedItemMap(
  companyId: string,
  itemIds: string[],
): Promise<Map<string, string>> {
  const ids = Array.from(new Set(itemIds.filter(Boolean)));
  if (!ids.length) return new Map();

  const { data, error } = await supabase
    .from('inventory_movements')
    .select('related_id, created_at')
    .eq('company_id', companyId)
    .eq('related_table', 'eirsaliye_item_import')
    .in('related_id', ids);

  if (error || !data?.length) {
    if (error) console.error('Import status fetch failed:', error);
    return new Map();
  }

  const latestByItem = new Map<string, string>();
  for (const row of data) {
    const itemId = (row.related_id || '') as string;
    const createdAt = row.created_at || '';
    if (!itemId || !createdAt) continue;

    const prev = latestByItem.get(itemId);
    if (!prev || new Date(createdAt) > new Date(prev)) {
      latestByItem.set(itemId, createdAt);
    }
  }
  return latestByItem;
}

async function fetchManualReceivedItemMap(itemIds: string[]): Promise<Map<string, string>> {
  const ids = Array.from(new Set(itemIds.filter(Boolean)));
  if (!ids.length) return new Map();

  const { data, error } = await supabase
    .from('eirsaliye_items')
    .select('*')
    .in('id', ids);

  if (error || !data?.length) {
    if (error) console.error('Manual receive status fetch failed:', error);
    return new Map();
  }

  const manualByItem = new Map<string, string>();
  for (const row of data as Array<Record<string, unknown>>) {
    const itemId = typeof row.id === 'string' ? row.id : '';
    const manualReceived = row.manual_received === true;
    const receivedAtRaw = row.manual_received_at;
    const receivedAt =
      typeof receivedAtRaw === 'string' && receivedAtRaw
        ? receivedAtRaw
        : new Date(0).toISOString();

    if (!itemId || !manualReceived) continue;
    manualByItem.set(itemId, receivedAt);
  }

  return manualByItem;
}

function buildItemReceiptMap(
  stockImportedMap: Map<string, string>,
  manualReceivedMap: Map<string, string>,
): Map<string, { mode: 'stock_import' | 'manual_received'; receivedAt: string }> {
  const receiptMap = new Map<string, { mode: 'stock_import' | 'manual_received'; receivedAt: string }>();

  for (const [itemId, receivedAt] of stockImportedMap.entries()) {
    receiptMap.set(itemId, { mode: 'stock_import', receivedAt });
  }

  for (const [itemId, receivedAt] of manualReceivedMap.entries()) {
    if (!receiptMap.has(itemId)) {
      receiptMap.set(itemId, { mode: 'manual_received', receivedAt });
    }
  }

  return receiptMap;
}

// ─── Service ───────────────────────────────────────────────────────────────

export const eirsaliyeService = {
  // ── Connection Management ──

  async getConnectionStatus(companyId: string): Promise<ConnectionStatus> {
    const { data, error } = await invokeAuthedFunction<ConnectionStatus>('eirsaliye-email-auth', {
      action: 'status',
    });

    // Fallback: query directly if edge function not deployed
    if (error) {
      const { data: connections } = await supabase
        .from('email_connections')
        .select('id, provider, email_address, is_active, last_sync_at, last_sync_status, last_sync_error, created_at')
        .eq('company_id', companyId);

      const list = (connections || []) as EmailConnection[];
      return {
        connections: list,
        gmail: list.find(c => c.provider === 'gmail') || null,
        outlook: list.find(c => c.provider === 'outlook') || null,
      };
    }

    return data as ConnectionStatus;
  },

  async initiateOAuth(provider: 'gmail' | 'outlook'): Promise<string> {
    const { data, error } = await invokeAuthedFunction<{ authUrl: string }>('eirsaliye-email-auth', {
      action: 'initiate',
      provider,
    });
    if (error) throw new Error(error.message || 'OAuth initiation failed');
    if (!data?.authUrl) throw new Error('OAuth URL could not be generated');
    return data.authUrl;
  },

  async disconnectProvider(companyId: string, provider: 'gmail' | 'outlook'): Promise<void> {
    const { error } = await invokeAuthedFunction<{ success: boolean }>('eirsaliye-email-auth', {
      action: 'disconnect',
      provider,
    });
    if (error) throw new Error(error.message || 'Disconnect failed');
  },

  // ── Sync ──

  async triggerSync(companyId: string, connectionId?: string): Promise<SyncResult> {
    const { data, error } = await invokeAuthedFunction<SyncResult>('eirsaliye-sync', {
      action: 'sync',
      connection_id: connectionId || null,
    });
    if (error) throw new Error(error.message || 'Sync failed');
    return data as SyncResult;
  },

  async retryEmail(emailId: string): Promise<{ success: boolean }> {
    const { data, error } = await invokeAuthedFunction<{ success: boolean }>('eirsaliye-sync', {
      action: 'retry',
      email_id: emailId,
    });
    if (error) throw new Error(error.message || 'Retry failed');
    return data ?? { success: false };
  },

  // ── Irsaliye Queries ──

  async getEirsaliyes(companyId: string, options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ data: EirsaliyeRow[]; count: number }> {
    const page = options?.page || 0;
    const pageSize = options?.pageSize || 10;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('eirsaliyes')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .order('irsaliye_tarihi', { ascending: false });

    if (options?.search) {
      query = query.or(`irsaliye_no.ilike.%${options.search}%,bayi_kodu.ilike.%${options.search}%,sube.ilike.%${options.search}%,erp_irsaliye_no.ilike.%${options.search}%`);
    }

    if (options?.status) {
      query = query.eq('status', options.status);
    }

    if (options?.dateFrom) {
      query = query.gte('irsaliye_tarihi', options.dateFrom);
    }
    if (options?.dateTo) {
      query = query.lte('irsaliye_tarihi', options.dateTo);
    }

    query = query.range(from, to);

    const { data, error, count } = await query;
    if (error) throw error;

    const rows = (data || []) as EirsaliyeRow[];
    if (!rows.length) return { data: rows, count: count || 0 };

    const irsaliyeIds = rows.map(r => r.id);
    const irsaliyeDateById = new Map(rows.map(r => [r.id, r.irsaliye_tarihi]));
    const maxDate = rows.reduce((max, row) => (
      row.irsaliye_tarihi > max ? row.irsaliye_tarihi : max
    ), rows[0].irsaliye_tarihi);

    const { data: itemRows, error: itemsError } = await supabase
      .from('eirsaliye_items')
      .select('id, eirsaliye_id, malzeme_no, miktar')
      .in('eirsaliye_id', irsaliyeIds);

    if (itemsError || !itemRows?.length) {
      if (itemsError) console.error('Failed to fetch eirsaliye items for cost:', itemsError);
      return {
        data: rows.map(row => ({
          ...row,
          estimated_total_cost: 0,
          all_items_stock_imported: false,
        })),
        count: count || 0,
      };
    }

    const importedMap = await fetchImportedItemMap(
      companyId,
      itemRows.map(item => item.id),
    );
    const manualReceivedMap = await fetchManualReceivedItemMap(itemRows.map(item => item.id));
    const receiptMap = buildItemReceiptMap(importedMap, manualReceivedMap);

    const importStatsByIrsaliye = new Map<string, { total: number; imported: number }>();
    for (const item of itemRows) {
      const eid = item.eirsaliye_id;
      if (!importStatsByIrsaliye.has(eid)) {
        importStatsByIrsaliye.set(eid, { total: 0, imported: 0 });
      }
      const agg = importStatsByIrsaliye.get(eid)!;
      agg.total += 1;
      if (receiptMap.has(item.id)) agg.imported += 1;
    }

    const skus = Array.from(
      new Set(itemRows.map(item => normalizeSku(item.malzeme_no)).filter(Boolean)),
    );
    const marsPriceMap = await fetchMarsPriceMapBySku(skus, maxDate);

    const totalByIrsaliye = new Map<string, number>();
    for (const item of itemRows) {
      const irsaliyeId = item.eirsaliye_id;
      if (!irsaliyeId) continue;

      const sku = normalizeSku(item.malzeme_no);
      const irsaliyeDate = irsaliyeDateById.get(irsaliyeId) || maxDate;
      const unitCost = sku ? pickMarsCostForDate(marsPriceMap.get(sku), irsaliyeDate) : null;
      const quantity = asNumber(item.miktar);
      const lineCost = (unitCost ?? 0) * quantity;

      totalByIrsaliye.set(irsaliyeId, (totalByIrsaliye.get(irsaliyeId) || 0) + lineCost);
    }

    const enrichedRows = rows.map(row => {
      const agg = importStatsByIrsaliye.get(row.id);
      const all_items_stock_imported = !!(
        agg &&
        agg.total > 0 &&
        agg.imported === agg.total
      );
      return {
        ...row,
        estimated_total_cost: Number((totalByIrsaliye.get(row.id) || 0).toFixed(2)),
        all_items_stock_imported,
      };
    });

    return { data: enrichedRows, count: count || 0 };
  },

  async getEirsaliyeById(id: string): Promise<EirsaliyeDetail | null> {
    const { data, error } = await supabase
      .from('eirsaliyes')
      .select(`
        *,
        eirsaliye_items(*),
        eirsaliye_emails!email_id(
          from_address,
          subject,
          received_at,
          provider_message_id
        ),
        eirsaliye_attachments!attachment_id(
          file_name,
          file_size_bytes,
          storage_path
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    const detail = data as unknown as EirsaliyeDetail;
    const itemIds = detail.eirsaliye_items?.map(item => item.id) || [];
    const importedMap = await fetchImportedItemMap(detail.company_id, itemIds);
    const manualReceivedMap = await fetchManualReceivedItemMap(itemIds);
    const receiptMap = buildItemReceiptMap(importedMap, manualReceivedMap);

    const enrichedItems = (detail.eirsaliye_items || []).map(item => ({
      ...item,
      stock_imported: receiptMap.has(item.id),
      stock_imported_at: receiptMap.get(item.id)?.receivedAt || null,
      manual_received: receiptMap.get(item.id)?.mode === 'manual_received',
      manual_received_at:
        receiptMap.get(item.id)?.mode === 'manual_received'
          ? receiptMap.get(item.id)?.receivedAt || null
          : null,
      received_mode: receiptMap.get(item.id)?.mode || null,
    }));

    const documentDate = detail.irsaliye_tarihi || new Date().toISOString().split('T')[0];
    const skus = Array.from(
      new Set(enrichedItems.map(item => normalizeSku(item.malzeme_no)).filter(Boolean)),
    );
    const marsPriceMap = await fetchMarsPriceMapBySku(skus, documentDate);

    let estimatedTotalCost = 0;
    for (const item of enrichedItems) {
      const sku = normalizeSku(item.malzeme_no);
      const unitCost = sku ? pickMarsCostForDate(marsPriceMap.get(sku), documentDate) : null;
      estimatedTotalCost += (unitCost ?? 0) * asNumber(item.miktar);
    }

    return {
      ...detail,
      eirsaliye_items: enrichedItems,
      estimated_total_cost: Number(estimatedTotalCost.toFixed(2)),
    };
  },

  // ── Stats ──

  async getStats(companyId: string): Promise<EirsaliyeStats> {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];

    // Total count
    const { count: total_count } = await supabase
      .from('eirsaliyes')
      .select('id', { count: 'exact', head: true })
      .eq('company_id', companyId);

    // This month count
    const { count: this_month_count } = await supabase
      .from('eirsaliyes')
      .select('id', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .gte('irsaliye_tarihi', firstOfMonth);

    // Total adet
    const { data: adetData } = await supabase
      .from('eirsaliyes')
      .select('toplam_adet')
      .eq('company_id', companyId);
    const total_adet = (adetData || []).reduce((sum, r) => sum + (r.toplam_adet || 0), 0);

    // Needs review
    const { count: needs_review_count } = await supabase
      .from('eirsaliyes')
      .select('id', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .eq('status', 'needs_review');

    // Last sync
    const { data: connections } = await supabase
      .from('email_connections')
      .select('last_sync_at')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('last_sync_at', { ascending: false })
      .limit(1);

    return {
      total_count: total_count || 0,
      this_month_count: this_month_count || 0,
      total_adet,
      needs_review_count: needs_review_count || 0,
      last_sync_at: connections?.[0]?.last_sync_at || null,
    };
  },

  // ── Daily Totals ──

  async getDailyTotals(companyId: string, date: string): Promise<DailyTotals> {
    const { data: irsaliyes } = await supabase
      .from('eirsaliyes')
      .select('id, toplam_adet')
      .eq('company_id', companyId)
      .eq('irsaliye_tarihi', date);

    const irsaliye_count = irsaliyes?.length || 0;
    const total_adet = (irsaliyes || []).reduce((sum, r) => sum + (r.toplam_adet || 0), 0);

    // Get unique products
    const irsaliyeIds = (irsaliyes || []).map(r => r.id);
    let unique_products = 0;

    if (irsaliyeIds.length > 0) {
      const { data: items } = await supabase
        .from('eirsaliye_items')
        .select('malzeme_no')
        .in('eirsaliye_id', irsaliyeIds);

      const uniqueNos = new Set((items || []).map(i => i.malzeme_no).filter(Boolean));
      unique_products = uniqueNos.size;
    }

    return { date, irsaliye_count, total_adet, unique_products };
  },

  async getDailyItems(companyId: string, date: string): Promise<Array<EirsaliyeItemRow & { irsaliye_no: string }>> {
    const { data: irsaliyes } = await supabase
      .from('eirsaliyes')
      .select('id, irsaliye_no')
      .eq('company_id', companyId)
      .eq('irsaliye_tarihi', date);

    if (!irsaliyes?.length) return [];

    const irsaliyeIds = irsaliyes.map(r => r.id);
    const irsaliyeMap = new Map(irsaliyes.map(r => [r.id, r.irsaliye_no]));

    const { data: items } = await supabase
      .from('eirsaliye_items')
      .select('*')
      .in('eirsaliye_id', irsaliyeIds)
      .order('sira_no');

    const itemList = items || [];
    const importedMap = await fetchImportedItemMap(companyId, itemList.map(item => item.id));
    const manualReceivedMap = await fetchManualReceivedItemMap(itemList.map(item => item.id));
    const receiptMap = buildItemReceiptMap(importedMap, manualReceivedMap);

    return itemList.map(item => ({
      ...item,
      irsaliye_no: irsaliyeMap.get(item.eirsaliye_id) || '',
      stock_imported: receiptMap.has(item.id),
      stock_imported_at: receiptMap.get(item.id)?.receivedAt || null,
      manual_received: receiptMap.get(item.id)?.mode === 'manual_received',
      manual_received_at:
        receiptMap.get(item.id)?.mode === 'manual_received'
          ? receiptMap.get(item.id)?.receivedAt || null
          : null,
      received_mode: receiptMap.get(item.id)?.mode || null,
    }));
  },

  async prepareDailyStockImport(
    companyId: string,
    date: string,
    itemIds: string[],
    marsPriceTier: MarsPriceTier = 'pesin',
  ): Promise<DailyStockImportPrefillItem[]> {
    const ids = Array.from(new Set(itemIds.filter(Boolean)));
    if (!ids.length) return [];

    const { data: irsaliyes, error: irsaliyeError } = await supabase
      .from('eirsaliyes')
      .select('id, irsaliye_no, irsaliye_tarihi')
      .eq('company_id', companyId)
      .eq('irsaliye_tarihi', date);

    if (irsaliyeError) throw irsaliyeError;
    if (!irsaliyes?.length) return [];

    const irsaliyeMap = new Map(irsaliyes.map(row => [row.id, row]));
    const allowedIrsaliyeIds = irsaliyes.map(row => row.id);

    const { data: rawItems, error: itemsError } = await supabase
      .from('eirsaliye_items')
      .select('id, eirsaliye_id, sira_no, malzeme_no, malzeme_tanimi, miktar, matched_product_id')
      .eq('company_id', companyId)
      .in('id', ids)
      .in('eirsaliye_id', allowedIrsaliyeIds)
      .order('sira_no');

    if (itemsError) throw itemsError;
    if (!rawItems?.length) return [];

    const importedMap = await fetchImportedItemMap(companyId, rawItems.map(item => item.id));
    const manualReceivedMap = await fetchManualReceivedItemMap(rawItems.map(item => item.id));
    const receiptMap = buildItemReceiptMap(importedMap, manualReceivedMap);
    const importableItems = rawItems.filter(item => !receiptMap.has(item.id));
    if (!importableItems.length) return [];

    const skus = Array.from(
      new Set(importableItems.map(item => normalizeSku(item.malzeme_no)).filter(Boolean)),
    );

    const { data: products, error: productsError } = skus.length
      ? await supabase
          .from('products')
          .select('id, item_code, name, category, current_cost, initial_cost')
          .eq('company_id', companyId)
          .in('item_code', skus)
      : { data: [], error: null };

    if (productsError) throw productsError;

    const productMap = new Map<string, {
      id: string;
      name: string;
      category: string;
      current_cost: number | null;
      initial_cost: number | null;
    }>();

    for (const product of products || []) {
      const sku = normalizeSku(product.item_code);
      if (!sku) continue;
      productMap.set(sku, {
        id: product.id,
        name: product.name || '',
        category: product.category || '',
        current_cost: product.current_cost,
        initial_cost: product.initial_cost,
      });
    }

    const marsPriceMap = await fetchMarsPriceMapBySku(skus, date);

    return importableItems.map(item => {
      const sku = normalizeSku(item.malzeme_no);
      const product = sku ? productMap.get(sku) : undefined;
      const marsCost = sku ? pickMarsCostForDate(marsPriceMap.get(sku), date, marsPriceTier) : null;

      let cost = 0;
      let costSource: DailyStockImportPrefillItem['cost_source'] = 'fallback_zero';

      if (marsCost !== null) {
        cost = marsCost;
        costSource = toMarsCostSource(marsPriceTier);
      } else if (product?.current_cost !== null && product?.current_cost !== undefined) {
        cost = asNumber(product.current_cost);
        costSource = 'products.current_cost';
      } else if (product?.initial_cost !== null && product?.initial_cost !== undefined) {
        cost = asNumber(product.initial_cost);
        costSource = 'products.initial_cost';
      }

      return {
        eirsaliye_item_id: item.id,
        eirsaliye_no: irsaliyeMap.get(item.eirsaliye_id)?.irsaliye_no || '',
        product_code: item.malzeme_no || '',
        product_name: product?.name || item.malzeme_tanimi || '',
        category: product?.category || '',
        quantity: asNumber(item.miktar),
        cost: Number(cost.toFixed(2)),
        cost_source: costSource,
        cost_source_label: formatCostSourceLabel(costSource),
        matched_product_id: product?.id || item.matched_product_id || null,
      };
    });
  },

  async getDailyIrsaliyes(companyId: string, date: string): Promise<EirsaliyeRow[]> {
    const { data, error } = await supabase
      .from('eirsaliyes')
      .select('*')
      .eq('company_id', companyId)
      .eq('irsaliye_tarihi', date)
      .order('irsaliye_zamani');

    if (error) throw error;
    return data || [];
  },

  // ── Sync Logs ──

  async getSyncLogs(companyId: string, limit = 10): Promise<SyncLogRow[]> {
    const { data, error } = await supabase
      .from('eirsaliye_sync_logs')
      .select('*')
      .eq('company_id', companyId)
      .order('started_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async markItemsManuallyReceived(companyId: string, itemIds: string[]): Promise<number> {
    const ids = Array.from(new Set(itemIds.filter(Boolean)));
    if (!ids.length) return 0;

    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from('eirsaliye_items')
      .update({
        manual_received: true,
        manual_received_at: nowIso,
      } as unknown as EirsaliyeItemUpdate)
      .eq('company_id', companyId)
      .in('id', ids)
      .select('id');

    if (error) throw error;
    return data?.length || 0;
  },

  // ── Status Update ──

  async updateStatus(id: string, status: string): Promise<void> {
    const { error } = await supabase
      .from('eirsaliyes')
      .update({ status })
      .eq('id', id);

    if (error) throw error;
  },
};
